/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { create } from 'zustand'
import { Project, saveProject, loadProject, listProjects, createNewProject, ChatMessage, PeerResponse, JudgeResult, saveDirectoryHandle, getDirectoryHandle, PeerConfig, hasWorkspace, listWorkspaceProjects, loadProjectFromFileSystem, saveProjectToFileSystem, createProjectInWorkspace, getWorkspaceHandle } from '@/lib/storage'
import { writeFile, deleteFile as deleteFileFromFS } from '@/lib/filesystem'
import { downloadGitHubRepo, parseGitHubUrl, getLatestCommitSha, getDefaultBranch, checkForUpdates, getChangedFiles } from '@/lib/github-service'
import { initializeHivemindFolder, hasHivemindFolder, autoConvertToMoth } from '@/lib/hivemind-init'
import { saveProjectToCloud, loadProjectFromCloud, listCloudProjects } from '@/lib/cloud-storage'
import { auth } from '@/lib/firebase'

interface GitHubDuplicateInfo {
  existingProject: Project
  repoInfo: { owner: string; repo: string; fullName: string }
  repoUrl: string
  repoName: string
  onProgress?: (message: string, progress: number) => void
}

interface ProjectState {
  currentProject: Project | null
  currentProjectHandle: FileSystemDirectoryHandle | null // Handle to project directory
  projects: Project[]
  isLoading: boolean
  error: string | null

  // GitHub duplicate detection
  githubDuplicateInfo: GitHubDuplicateInfo | null
  setGitHubDuplicateInfo: (info: GitHubDuplicateInfo | null) => void
  handleGitHubDuplicateAction: (action: 'open' | 'update' | 'new') => Promise<void>

  // Actions
  loadProjects: () => Promise<void>
  createProject: (name: string, customPeers?: PeerConfig[]) => Promise<void>
  loadRepoAsProject: (repoPath: string) => Promise<void>
  loadGitHubRepoAsProject: (repoUrl: string, repoName: string, onProgress?: (message: string, progress: number) => void) => Promise<void>
  openProject: (id: string) => Promise<void>
  openProjectFromHandle: (handle: FileSystemDirectoryHandle) => Promise<void>
  saveCurrentProject: () => Promise<void>
  updateProjectSettings: (settings: Project['settings']) => Promise<void>
  updateFile: (filename: string, content: string) => Promise<void>
  deleteFile: (filename: string) => Promise<void>
  setProjectName: (name: string) => void
  updateProject: (project: Project) => Promise<void>
  updateThread: (messages: ChatMessage[], peerResponses: Record<string, PeerResponse | null>, judgeResult: JudgeResult | null) => void
  saveThread: (messages: ChatMessage[], peerResponses: Record<string, PeerResponse | null>, judgeResult: JudgeResult | null) => Promise<void>

  // GitHub sync actions
  findExistingGitHubProject: (owner: string, repo: string) => Project | null
  checkGitHubUpdates: (projectId: string) => Promise<{ hasUpdates: boolean; commitsBehind?: number } | null>
  updateGitHubProject: (projectId: string, onProgress?: (message: string, progress: number) => void) => Promise<void>

  // Cloud actions
  saveToCloud: () => Promise<void>
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  currentProject: null,
  currentProjectHandle: null,
  projects: [],
  isLoading: false,
  error: null,
  githubDuplicateInfo: null,

  setGitHubDuplicateInfo: (info: GitHubDuplicateInfo | null) => {
    set({ githubDuplicateInfo: info })
  },

  loadProjects: async () => {
    set({ isLoading: true, error: null })
    try {
      // Check if workspace is set up
      const workspaceExists = await hasWorkspace()
      let projects: Project[] = []

      if (workspaceExists) {
        // Load projects from workspace
        const workspaceProjects = await listWorkspaceProjects()
        console.log('Loaded projects from workspace:', workspaceProjects.length, workspaceProjects.map(p => p.name))

        // Convert workspace projects to Project objects
        const fsProjects = await Promise.all(
          workspaceProjects.map(async ({ name, handle }) => {
            try {
              const project = await loadProjectFromFileSystem(handle)
              if (project) project.storageType = 'local'
              return project
            } catch (err) {
              console.error(`Failed to load project ${name}:`, err)
              return null
            }
          })
        ).then(projects => projects.filter((p): p is Project => p !== null))
        projects = [...projects, ...fsProjects]
      } else {
        // Fall back to IndexedDB
        // Pass current user ID to filter projects
        const user = auth.currentUser
        if (user) {
          const dbProjects = await listProjects(user.uid)
          dbProjects.forEach(p => p.storageType = 'local')
          projects = [...projects, ...dbProjects]
        }
      }

      // Load Cloud Projects if logged in
      if (auth.currentUser) {
        try {
          const cloudProjects = await listCloudProjects()
          cloudProjects.forEach(p => p.storageType = 'cloud')
          // Merge cloud projects, avoiding duplicates if any (though IDs should be unique)
          const existingIds = new Set(projects.map(p => p.id))
          cloudProjects.forEach(p => {
            if (!existingIds.has(p.id)) {
              projects.push(p)
            }
          })
        } catch (err) {
          console.error('Failed to load cloud projects:', err)
        }
      }

      set({ projects, isLoading: false })
    } catch (error) {
      console.error('Error loading projects:', error)
      set({ error: (error as Error).message, isLoading: false })
    }
  },

  createProject: async (name: string, customPeers?: PeerConfig[]) => {
    set({ isLoading: true, error: null })
    try {
      const user = auth.currentUser
      const newProject = createNewProject(name, user?.uid)

      // If custom peers provided, replace the default peers
      if (customPeers && customPeers.length > 0 && newProject.settings) {
        newProject.settings.peers = customPeers
      }

      // Initialize .hivemind folder
      initializeHivemindFolder(newProject)

      // Check if workspace is set up
      const workspaceExists = await hasWorkspace()

      if (workspaceExists) {
        // Create project folder in workspace
        const projectHandle = await createProjectInWorkspace(name)
        if (projectHandle) {
          // Save project to file system
          await saveProjectToFileSystem(projectHandle, newProject)

          // Load all projects
          const projects = await get().loadProjects()

          // Open the new project
          set({ currentProject: newProject, currentProjectHandle: projectHandle, isLoading: false })
        } else {
          throw new Error('Failed to create project folder in workspace')
        }
      } else {
        // Fall back to IndexedDB
        await saveProject(newProject)
        const projects = await listProjects()
        set({ currentProject: newProject, projects, isLoading: false })
      }
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false })
    }
  },

  loadRepoAsProject: async () => {
    set({ isLoading: true, error: null })
    try {
      // Request directory access using File System Access API
      // @ts-ignore - File System Access API
      const dirHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
      })

      if (!dirHandle) {
        set({ isLoading: false })
        return
      }

      const repoName = dirHandle.name

      // Create a new project with the repo
      const now = Date.now()
      const project: Project = {
        id: `repo-${now}`,
        name: repoName,
        description: `Repository: ${repoName}`,
        createdAt: now,
        updatedAt: now,
        fileSystemPath: repoName, // Store the directory name for reference
        files: {},
        settings: {
          models: {
            gemini: 'google/gemini-2.5-pro',
            claude: 'anthropic/claude-sonnet-4.5',
            grok: 'x-ai/grok-4',
            judge: 'anthropic/claude-sonnet-4.5',
          },
          peers: [
            {
              id: 'peer-1',
              personaId: 'code-reviewer',
              personaName: 'Code Reviewer',
              model: 'google/gemini-2.5-pro',
              systemPrompt: `You are a meticulous code reviewer focused on quality, maintainability, and best practices. Analyze code for bugs, performance issues, security vulnerabilities, and adherence to coding standards.`,
              enabled: true,
              collapsed: false,
              color: 'text-purple-400',
              icon: 'gemini',
            },
            {
              id: 'peer-2',
              personaId: 'content-editor',
              personaName: 'Content Editor',
              model: 'anthropic/claude-sonnet-4.5',
              systemPrompt: `You are a skilled content editor focused on clarity, coherence, and engagement. Review documentation, comments, and user-facing text for readability and effectiveness.`,
              enabled: true,
              collapsed: false,
              color: 'text-orange-400',
              icon: 'claude',
            },
            {
              id: 'peer-3',
              personaId: 'innovation-advisor',
              personaName: 'Innovation Advisor',
              model: 'x-ai/grok-4',
              systemPrompt: `You are an innovation advisor focused on creative solutions and cutting-edge approaches. Suggest improvements, alternative architectures, and novel ideas.`,
              enabled: true,
              collapsed: false,
              color: 'text-blue-400',
              icon: 'grok',
            },
          ],
        },
        thread: {
          messages: [],
          peerResponses: {},
          judgeResult: null,
        },
      }

      // Store the directory handle in IndexedDB for later use
      await saveDirectoryHandle(project.id, dirHandle)

      // Create .hivemind folder with initial files
      try {
        // Create README in .hivemind folder
        const readmeContent = `# Hivemind Project

This folder contains Hivemind project data for **${repoName}**.

## Structure

- \`project.json\` - Project metadata and settings
- \`thread.json\` - Current conversation thread data
- \`chats/\` - Saved orchestrator chat sessions
- \`peers/\` - Saved peer responses
- \`inflated/\` - Inflated markdown documents
- \`judge/\` - Judge evaluations

## About HiveMind

HiveMind is a browser-based multi-LLM orchestration system that coordinates multiple AI personas through structured MOTH (Minimal Overhead Technical Hierarchy) packet exchanges.

Learn more: https://github.com/huikku/hivemind
`
        await writeFile(dirHandle, '.hivemind/README.md', readmeContent)

        // Create initial project.json
        const projectMetadata = {
          name: project.name,
          description: project.description,
          settings: project.settings,
          createdAt: project.createdAt,
          updatedAt: project.updatedAt,
        }
        await writeFile(dirHandle, '.hivemind/project.json', JSON.stringify(projectMetadata, null, 2))
      } catch (error) {
        console.error('Failed to create .hivemind folder:', error)
      }

      // Initialize .hivemind folder in project files
      initializeHivemindFolder(project)

      await saveProject(project)
      const projects = await listProjects()
      set({ currentProject: project, projects, isLoading: false })
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false })
    }
  },

  loadGitHubRepoAsProject: async (repoUrl: string, repoName: string, onProgress?: (message: string, progress: number) => void) => {
    set({ isLoading: true, error: null })
    try {
      onProgress?.('Parsing repository URL...', 5)

      const repoInfo = parseGitHubUrl(repoUrl)
      if (!repoInfo) {
        throw new Error('Invalid GitHub repository URL')
      }

      // Check if this repo already exists
      const existingProject = get().findExistingGitHubProject(repoInfo.owner, repoInfo.repo)
      if (existingProject) {
        console.log('[loadGitHubRepoAsProject] Duplicate detected:', existingProject.name)
        // Show modal and wait for user decision
        set({
          githubDuplicateInfo: {
            existingProject,
            repoInfo,
            repoUrl,
            repoName,
            onProgress
          },
          isLoading: false
        })
        return
      }

      console.log('[loadGitHubRepoAsProject] No duplicate found, proceeding with clone...')

      // Ask user where to save the repository
      onProgress?.('Select where to save repository...', 3)
      let parentDirHandle: FileSystemDirectoryHandle
      try {
        parentDirHandle = await (window as any).showDirectoryPicker({
          mode: 'readwrite',
          startIn: 'documents'
        })
      } catch (error) {
        // User cancelled
        console.log('[loadGitHubRepoAsProject] User cancelled folder selection')
        set({ isLoading: false })
        return
      }

      // Check if folder already exists
      const folderName = repoInfo.repo
      let folderExists = false
      try {
        await parentDirHandle.getDirectoryHandle(folderName, { create: false })
        folderExists = true
      } catch {
        // Folder doesn't exist, which is good
        folderExists = false
      }

      if (folderExists) {
        const confirmed = confirm(
          `A folder named "${folderName}" already exists in this location.\n\n` +
          `Do you want to overwrite it?\n\n` +
          `⚠️ WARNING: This will replace all existing files in that folder!`
        )
        if (!confirmed) {
          console.log('[loadGitHubRepoAsProject] User cancelled - folder already exists')
          set({ isLoading: false })
          return
        }
      }

      // Create or open the folder
      let repoDirHandle: FileSystemDirectoryHandle
      try {
        repoDirHandle = await parentDirHandle.getDirectoryHandle(folderName, { create: true })
        console.log('[loadGitHubRepoAsProject] Created/opened folder:', folderName, 'in', parentDirHandle.name)
      } catch (error) {
        throw new Error(`Failed to create folder "${folderName}". Make sure you have write permissions.`)
      }

      // Get the default branch and latest commit SHA
      onProgress?.('Fetching repository info...', 10)
      let branch: string
      let commitSha: string | undefined
      try {
        // Get the repository's default branch
        branch = await getDefaultBranch(repoInfo.owner, repoInfo.repo)
        console.log('[loadGitHubRepoAsProject] Default branch:', branch)

        // Get the latest commit SHA for that branch
        try {
          commitSha = await getLatestCommitSha(repoInfo.owner, repoInfo.repo, branch)
          console.log('[loadGitHubRepoAsProject] Latest commit SHA:', commitSha)
        } catch (error) {
          console.warn('[loadGitHubRepoAsProject] Could not fetch commit SHA:', error)
        }
      } catch (error) {
        console.error('[loadGitHubRepoAsProject] Could not fetch default branch:', error)
        branch = 'main' // Fallback to main if we can't get repo info
      }

      // Download repository files
      const files = await downloadGitHubRepo(repoUrl, onProgress)

      onProgress?.('Writing files to folder...', 80)

      // Write all downloaded files to the folder
      let filesWritten = 0
      const totalFiles = files.size
      for (const [filePath, content] of files.entries()) {
        try {
          await writeFile(repoDirHandle, filePath, content)
          filesWritten++
          if (filesWritten % 10 === 0) {
            onProgress?.(`Writing files... (${filesWritten}/${totalFiles})`, 80 + (filesWritten / totalFiles) * 10)
          }
        } catch (error) {
          console.error(`Failed to write file ${filePath}:`, error)
        }
      }

      onProgress?.('Creating project...', 90)

      // Create a new project with the downloaded files
      const now = Date.now()
      const project: Project = {
        id: `repo-${now}`,
        name: repoName,
        description: `GitHub: ${repoInfo.fullName}`,
        createdAt: now,
        updatedAt: now,
        fileSystemPath: folderName, // Mark as file system project
        files: {}, // Files are on disk, not in memory
        github: {
          owner: repoInfo.owner,
          repo: repoInfo.repo,
          branch,
          lastCommitSha: commitSha,
          lastSyncedAt: now,
          url: repoUrl,
        },
        settings: {
          models: {
            orchestrator: 'anthropic/claude-3-5-sonnet-20241022',
            judge: 'anthropic/claude-3-5-sonnet-20241022',
          },
          peers: [
            {
              id: 'peer-1',
              personaId: 'code-reviewer',
              personaName: 'Code Reviewer',
              model: 'google/gemini-2.0-flash-exp',
              systemPrompt: `You are a meticulous code reviewer focused on quality, maintainability, and best practices. Analyze code for bugs, performance issues, security vulnerabilities, and adherence to coding standards.`,
              enabled: true,
              collapsed: false,
              color: 'text-purple-400',
              icon: 'gemini',
            },
            {
              id: 'peer-2',
              personaId: 'content-editor',
              personaName: 'Content Editor',
              model: 'anthropic/claude-3-5-sonnet-20241022',
              systemPrompt: `You are a skilled content editor focused on clarity, coherence, and engagement. Review documentation, comments, and user-facing text for readability and effectiveness.`,
              enabled: true,
              collapsed: false,
              color: 'text-orange-400',
              icon: 'claude',
            },
            {
              id: 'peer-3',
              personaId: 'innovation-advisor',
              personaName: 'Innovation Advisor',
              model: 'x-ai/grok-2-1212',
              systemPrompt: `You are an innovation advisor focused on creative solutions and cutting-edge approaches. Suggest improvements, alternative architectures, and novel ideas.`,
              enabled: true,
              collapsed: false,
              color: 'text-blue-400',
              icon: 'grok',
            },
          ],
        },
        thread: {
          messages: [],
          peerResponses: {},
          judgeResult: null,
        },
      }

      // Store the directory handle for future access
      await saveDirectoryHandle(project.id, repoDirHandle)

      // Initialize .hivemind folder in the directory
      initializeHivemindFolder(project)

      // Write .hivemind files to the folder
      try {
        const readmeContent = `# Hivemind Project

This folder contains Hivemind project data for **${repoName}**.

## Structure

- \`.hivemind/\` - Hivemind configuration and data
- Repository files from GitHub: ${repoInfo.fullName}

## About HiveMind

HiveMind is a browser-based multi-LLM orchestration system that coordinates multiple AI personas through structured MOTH (Minimal Overhead Technical Hierarchy) packet exchanges.

Learn more: https://github.com/huikku/hivemind
`
        await writeFile(repoDirHandle, '.hivemind/README.md', readmeContent)

        // Create initial project.json
        const projectMetadata = {
          name: project.name,
          description: project.description,
          settings: project.settings,
          github: project.github,
          createdAt: project.createdAt,
          updatedAt: project.updatedAt,
        }
        await writeFile(repoDirHandle, '.hivemind/project.json', JSON.stringify(projectMetadata, null, 2))
      } catch (error) {
        console.error('Failed to create .hivemind folder:', error)
      }

      await saveProject(project)
      const projects = await listProjects()

      onProgress?.('Repository cloned successfully!', 100)

      console.log('[loadGitHubRepoAsProject] Setting current project:', project.name, 'ID:', project.id)
      console.log('[loadGitHubRepoAsProject] Files saved to:', folderName)
      set({ currentProject: project, projects, isLoading: false })
      console.log('[loadGitHubRepoAsProject] Current project set. Store state:', get().currentProject?.name)
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false })
      throw error
    }
  },

  openProject: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      let project = await loadProject(id)

      // If not found locally, try cloud
      if (!project && auth.currentUser) {
        try {
          project = await loadProjectFromCloud(id)
          if (project) project.storageType = 'cloud'
        } catch (err) {
          console.error('Failed to load from cloud:', err)
        }
      }

      if (project) {
        console.log('Opened project:', project.name, 'ID:', id)

        // Initialize .hivemind folder if it doesn't exist (for legacy projects)
        initializeHivemindFolder(project)

        // Auto-convert .md files to .moth format in the background
        autoConvertToMoth(project, (message) => {
          console.log('[MOTH Auto-Convert]', message)
        }).catch((error) => {
          console.error('[MOTH Auto-Convert] Error:', error)
        })

        // Save the project if we added .hivemind files
        await saveProject(project)

        set({ currentProject: project, isLoading: false })
        // Store last opened project ID in localStorage
        localStorage.setItem('lastProjectId', id)
        console.log('Saved lastProjectId to localStorage:', id)
      } else {
        console.warn('Project not found:', id)
        set({ error: 'Project not found', isLoading: false })
      }
    } catch (error) {
      console.error('Error opening project:', error)
      set({ error: (error as Error).message, isLoading: false })
    }
  },

  saveToCloud: async () => {
    const { currentProject } = get()
    if (!currentProject || !auth.currentUser) return

    set({ isLoading: true, error: null })
    try {
      // Update project to be cloud type
      const cloudProject = {
        ...currentProject,
        storageType: 'cloud' as const,
        userId: auth.currentUser.uid,
        updatedAt: Date.now()
      }

      await saveProjectToCloud(cloudProject)

      // Also save locally as backup/cache
      await saveProject(cloudProject)

      set({ currentProject: cloudProject, isLoading: false })
      await get().loadProjects() // Refresh list
      console.log('Project saved to cloud:', cloudProject.name)
    } catch (error) {
      console.error('Error saving to cloud:', error)
      set({ error: (error as Error).message, isLoading: false })
    }
  },

  openProjectFromHandle: async (handle: FileSystemDirectoryHandle) => {
    set({ isLoading: true, error: null })
    try {
      const project = await loadProjectFromFileSystem(handle)
      if (project) {
        console.log('Opened project from file system:', project.name)

        // Auto-convert .md files to .moth format in the background
        autoConvertToMoth(project, (message) => {
          console.log('[MOTH Auto-Convert]', message)
        }).catch((error) => {
          console.error('[MOTH Auto-Convert] Error:', error)
        })

        set({ currentProject: project, currentProjectHandle: handle, isLoading: false })
        // Store last opened project name in localStorage
        localStorage.setItem('lastProjectName', project.name)
      } else {
        console.warn('Failed to load project from directory')
        set({ error: 'Failed to load project', isLoading: false })
      }
    } catch (error) {
      console.error('Error opening project from handle:', error)
      set({ error: (error as Error).message, isLoading: false })
    }
  },

  saveCurrentProject: async () => {
    const { currentProject, currentProjectHandle } = get()
    if (!currentProject) return

    set({ isLoading: true, error: null })
    try {
      currentProject.updatedAt = Date.now()

      // Save to file system if we have a handle
      if (currentProjectHandle) {
        await saveProjectToFileSystem(currentProjectHandle, currentProject)
        console.log('Saved project to file system:', currentProject.name)
      } else if (currentProject.storageType === 'cloud') {
        // Save to Cloud
        await saveProjectToCloud(currentProject)
        console.log('Saved project to Cloud:', currentProject.name)
      } else {
        // Fall back to IndexedDB
        await saveProject(currentProject)
        console.log('Saved project to IndexedDB:', currentProject.name)
      }

      const projects = await get().loadProjects()
      set({ isLoading: false })
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false })
    }
  },

  updateProjectSettings: async (settings: Project['settings']) => {
    const { currentProject } = get()
    if (!currentProject) return

    set({ isLoading: true, error: null })
    try {
      const updatedProject = {
        ...currentProject,
        settings,
        updatedAt: Date.now(),
      }
      await saveProject(updatedProject)
      const projects = await listProjects()
      set({ currentProject: updatedProject, projects, isLoading: false })
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false })
    }
  },

  updateFile: async (filename: string, content: string) => {
    const { currentProject } = get()
    if (!currentProject) return

    // Update in-memory project
    set({
      currentProject: {
        ...currentProject,
        files: {
          ...currentProject.files,
          [filename]: content
        }
      }
    })

    // If project has a directory handle, write to file system
    if (currentProject.fileSystemPath) {
      try {
        const dirHandle = await getDirectoryHandle(currentProject.id)
        if (dirHandle) {
          // All Hivemind files go in .hivemind folder
          const hivemindPath = `.hivemind/${filename}`
          await writeFile(dirHandle, hivemindPath, content)
        }
      } catch (error) {
        console.error('Failed to write file to file system:', error)
      }
    }
  },

  deleteFile: async (filename: string) => {
    const { currentProject } = get()
    if (!currentProject) return

    const files = { ...currentProject.files }
    delete files[filename]

    set({
      currentProject: {
        ...currentProject,
        files
      }
    })

    // If project has a directory handle, delete from file system
    if (currentProject.fileSystemPath) {
      try {
        const dirHandle = await getDirectoryHandle(currentProject.id)
        if (dirHandle) {
          // All Hivemind files are in .hivemind folder
          const hivemindPath = `.hivemind/${filename}`
          await deleteFileFromFS(dirHandle, hivemindPath)
        }
      } catch (error) {
        console.error('Failed to delete file from file system:', error)
      }
    }
  },

  setProjectName: (name: string) => {
    const { currentProject } = get()
    if (!currentProject) return

    set({
      currentProject: {
        ...currentProject,
        name
      }
    })
  },

  updateProject: async (project: Project) => {
    set({ currentProject: project })
    await get().saveCurrentProject()
  },

  updateThread: (messages: ChatMessage[], peerResponses: Record<string, PeerResponse | null>, judgeResult: JudgeResult | null) => {
    const { currentProject } = get()
    if (!currentProject) return

    // Build peer history for each peer that has preserveContext enabled
    const peerHistory: Record<string, ChatMessage[]> = {}
    const peers = currentProject.settings?.peers || []

    peers.forEach(peer => {
      if (peer.preserveContext) {
        // Get existing history or initialize empty array
        const existingHistory = currentProject.thread?.peerHistory?.[peer.id] || []

        // Add the latest user prompt and peer response to history
        const latestUserMessage = messages[messages.length - 1]
        const latestPeerResponse = peerResponses[peer.id]

        if (latestUserMessage && latestPeerResponse) {
          peerHistory[peer.id] = [
            ...existingHistory,
            { id: `user-${Date.now()}`, timestamp: Date.now(), role: 'user', content: latestUserMessage.content },
            { id: `peer-${Date.now()}`, timestamp: Date.now(), role: 'assistant', content: latestPeerResponse.content },
          ]
          console.log(`[Project] Updated peer history for ${peer.personaName}: ${peerHistory[peer.id].length} messages`)
        } else {
          peerHistory[peer.id] = existingHistory
        }
      }
    })

    set({
      currentProject: {
        ...currentProject,
        thread: {
          messages,
          peerResponses,
          judgeResult,
          peerHistory: Object.keys(peerHistory).length > 0 ? peerHistory : undefined,
        }
      }
    })
  },

  saveThread: async (messages: ChatMessage[], peerResponses: Record<string, PeerResponse | null>, judgeResult: JudgeResult | null) => {
    const { currentProject } = get()
    if (!currentProject) return

    // Build peer history for each peer that has preserveContext enabled
    const peerHistory: Record<string, ChatMessage[]> = {}
    const peers = currentProject.settings?.peers || []

    peers.forEach(peer => {
      if (peer.preserveContext) {
        // Get existing history or initialize empty array
        const existingHistory = currentProject.thread?.peerHistory?.[peer.id] || []

        // Add the latest user prompt and peer response to history
        const latestUserMessage = messages[messages.length - 1]
        const latestPeerResponse = peerResponses[peer.id]

        if (latestUserMessage && latestPeerResponse) {
          peerHistory[peer.id] = [
            ...existingHistory,
            { id: `user-${Date.now()}`, timestamp: Date.now(), role: 'user', content: latestUserMessage.content },
            { id: `peer-${Date.now()}`, timestamp: Date.now(), role: 'assistant', content: latestPeerResponse.content },
          ]
        } else {
          peerHistory[peer.id] = existingHistory
        }
      }
    })

    const updatedProject = {
      ...currentProject,
      thread: {
        messages,
        peerResponses,
        judgeResult,
        peerHistory: Object.keys(peerHistory).length > 0 ? peerHistory : undefined,
      },
      updatedAt: Date.now(),
    }

    try {
      await saveProject(updatedProject)
      set({ currentProject: updatedProject })

      // If project has a directory handle, save thread data to .hivemind folder
      if (updatedProject.fileSystemPath) {
        try {
          const dirHandle = await getDirectoryHandle(updatedProject.id)
          if (dirHandle) {
            const threadData = {
              messages,
              peerResponses,
              judgeResult,
              peerHistory: Object.keys(peerHistory).length > 0 ? peerHistory : undefined,
              updatedAt: updatedProject.updatedAt,
            }
            await writeFile(dirHandle, '.hivemind/thread.json', JSON.stringify(threadData, null, 2))
          }
        } catch (error) {
          console.error('Failed to save thread data to file system:', error)
        }
      }
    } catch (error) {
      console.error('Failed to save thread:', error)
    }
  },

  // GitHub sync methods
  findExistingGitHubProject: (owner: string, repo: string) => {
    const { projects } = get()
    return projects.find(
      p => p.github?.owner === owner && p.github?.repo === repo
    ) || null
  },

  checkGitHubUpdates: async (projectId: string) => {
    try {
      const project = await loadProject(projectId)
      if (!project?.github) {
        return null
      }

      const { owner, repo, branch, lastCommitSha } = project.github

      if (!lastCommitSha) {
        // No commit SHA stored, can't check for updates
        return null
      }

      const result = await checkForUpdates(owner, repo, branch, lastCommitSha)
      return result
    } catch (error) {
      console.error('Failed to check for updates:', error)
      return null
    }
  },

  updateGitHubProject: async (projectId: string, onProgress?: (message: string, progress: number) => void) => {
    set({ isLoading: true, error: null })
    try {
      const project = await loadProject(projectId)
      if (!project?.github) {
        throw new Error('This project is not linked to a GitHub repository')
      }

      const { owner, repo, branch, url } = project.github

      onProgress?.('Checking for updates...', 10)

      // Get latest commit SHA
      const latestSha = await getLatestCommitSha(owner, repo, branch)

      if (latestSha === project.github.lastCommitSha) {
        onProgress?.('Already up to date!', 100)
        set({ isLoading: false })
        return
      }

      onProgress?.('Downloading updated files...', 30)

      // Download fresh copy of repository
      const files = await downloadGitHubRepo(url, onProgress)

      onProgress?.('Updating project...', 90)

      // Update project with new files and commit SHA
      const updatedProject: Project = {
        ...project,
        files: Object.fromEntries(files),
        updatedAt: Date.now(),
        github: {
          ...project.github,
          lastCommitSha: latestSha,
          lastSyncedAt: Date.now(),
        },
      }

      await saveProject(updatedProject)
      const projects = await listProjects()

      // If this is the current project, update it in state
      const { currentProject } = get()
      if (currentProject?.id === projectId) {
        set({ currentProject: updatedProject })
      }

      set({ projects, isLoading: false })
      onProgress?.('Project updated successfully!', 100)
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false })
      throw error
    }
  },

  handleGitHubDuplicateAction: async (action: 'open' | 'update' | 'new') => {
    const { githubDuplicateInfo } = get()
    if (!githubDuplicateInfo) return

    const { existingProject, repoInfo, repoUrl, repoName, onProgress } = githubDuplicateInfo

    // Clear the modal
    set({ githubDuplicateInfo: null })

    if (action === 'open') {
      // Open the existing project
      await get().openProject(existingProject.id)
      onProgress?.('Opened existing project', 100)
      return
    }

    if (action === 'update') {
      // Update the existing project and open it
      await get().updateGitHubProject(existingProject.id, onProgress)
      await get().openProject(existingProject.id)
      return
    }

    if (action === 'new') {
      // Continue with cloning as new project
      set({ isLoading: true, error: null })
      try {
        onProgress?.('Fetching repository info...', 10)
        let branch: string
        let commitSha: string | undefined
        try {
          // Get the repository's default branch
          branch = await getDefaultBranch(repoInfo.owner, repoInfo.repo)
          console.log('[handleGitHubDuplicateAction] Default branch:', branch)

          // Get the latest commit SHA for that branch
          try {
            commitSha = await getLatestCommitSha(repoInfo.owner, repoInfo.repo, branch)
            console.log('[handleGitHubDuplicateAction] Latest commit SHA:', commitSha)
          } catch (error) {
            console.warn('[handleGitHubDuplicateAction] Could not fetch commit SHA:', error)
          }
        } catch (error) {
          console.error('[handleGitHubDuplicateAction] Could not fetch default branch:', error)
          branch = 'main' // Fallback to main if we can't get repo info
        }

        // Download repository files
        const files = await downloadGitHubRepo(repoUrl, onProgress)

        onProgress?.('Creating project...', 95)

        // Create a new project with GitHub metadata
        const project = createNewProject(repoName)
        project.files = Object.fromEntries(files)
        project.github = {
          owner: repoInfo.owner,
          repo: repoInfo.repo,
          branch,
          url: repoUrl,
          fullName: repoInfo.fullName,
          lastCommitSha: commitSha,
          lastSyncedAt: Date.now(),
        }

        // Initialize .hivemind folder
        initializeHivemindFolder(project)

        // Auto-convert .md files to .moth format in the background
        autoConvertToMoth(project, (message) => {
          console.log('[MOTH Auto-Convert]', message)
          onProgress?.(message, 97)
        }).catch((error) => {
          console.error('[MOTH Auto-Convert] Error:', error)
        })

        await saveProject(project)
        const projects = await listProjects()
        set({ currentProject: project, projects, isLoading: false })

        onProgress?.('Repository cloned successfully!', 100)
      } catch (error) {
        set({ error: (error as Error).message, isLoading: false })
        throw error
      }
    }
  },
}))

